// Character keys (Settings › Shortcuts): a key that types characters the
// keyboard doesn't have, such as ✓, ✗ or an em dash. Each slot holds the text it
// types and, optionally, alternates that take its place on each further
// press. The writer pastes or types the text; Ink Symbols
// (scripts/build-symbols-font.py) draws whatever the page's font lacks, so the
// characters look the same in every font and on every device.

import { normalizeShortcut } from '../shortcuts/keys'
import coverageRanges from './symbolCoverage.json'

export interface CharacterKey {
  id: string
  /** The shortcut, or '' for none yet. */
  key: string
  /** What the key types: the first on a press, each next one on a further press. */
  texts: string[]
}

/** The most characters one entry can hold, counted as the writer sees them. */
export const MAX_CHARACTERS = 8
/** The main text plus up to three alternates. */
export const MAX_TEXTS = 4

interface GraphemeSegmenter {
  segment(text: string): Iterable<{ segment: string }>
}

const Segmenter = typeof Intl !== 'undefined' ? (Intl as any).Segmenter : undefined
const segmenter: GraphemeSegmenter | null = Segmenter ? new Segmenter(undefined, { granularity: 'grapheme' }) : null

/** The characters of a text as the writer sees them: é, 👍🏽 or a flag is one each. */
export function graphemes(text: string): string[] {
  if (!segmenter) return [...text]
  return [...segmenter.segment(text)].map((part) => part.segment)
}

// Line breaks, tabs and other control characters never belong in a character key.
const UNWANTED = /[\p{Cc}\u2028\u2029]/gu

/** Cleans what was typed or pasted: composed form, no control characters, at most MAX_CHARACTERS. */
export function cleanCharacterText(raw: string): string {
  const text = raw.normalize('NFC').replace(UNWANTED, '')
  return graphemes(text).slice(0, MAX_CHARACTERS).join('')
}

export function newCharacterKeyId(): string {
  return Math.random().toString(36).slice(2, 10)
}

export function normalizeCharacterKeys(raw: unknown): CharacterKey[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  const out: CharacterKey[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const id = typeof item.id === 'string' && /^[a-z0-9]{1,16}$/.test(item.id) && !seen.has(item.id) ? item.id : newCharacterKeyId()
    seen.add(id)
    const key = typeof item.key === 'string' ? normalizeShortcut(item.key) ?? '' : ''
    const texts = Array.isArray(item.texts)
      ? item.texts.filter((text: unknown): text is string => typeof text === 'string').map(cleanCharacterText).slice(0, MAX_TEXTS)
      : []
    out.push({ id, key, texts: texts.length ? texts : [''] })
  }
  return out
}

/** The texts a slot types, in order, leaving out empty ones. */
export function characterTexts(slot: CharacterKey): string[] {
  return slot.texts.filter((text) => text !== '')
}

// Characters that can't be seen, or are easily mistaken for another, by name.
const NAMES: Record<string, string> = {
  ' ': 'space',
  '\u00a0': 'no-break space',
  '\u00ad': 'soft hyphen',
  '\u034f': 'combining grapheme joiner',
  '\u2002': 'en space',
  '\u2003': 'em space',
  '\u2004': 'third-em space',
  '\u2005': 'quarter-em space',
  '\u2006': 'sixth-em space',
  '\u2007': 'figure space',
  '\u2008': 'punctuation space',
  '\u2009': 'thin space',
  '\u200a': 'hair space',
  '\u200b': 'zero-width space',
  '\u200c': 'zero-width non-joiner',
  '\u200d': 'zero-width joiner',
  '\u200e': 'left-to-right mark',
  '\u200f': 'right-to-left mark',
  '‐': 'hyphen',
  '‑': 'non-breaking hyphen',
  '‒': 'figure dash',
  '–': 'en dash',
  '—': 'em dash',
  '―': 'horizontal bar',
  '\u202f': 'narrow no-break space',
  '\u205f': 'math space',
  '\u2060': 'word joiner',
  '−': 'minus sign',
  '\u3000': 'ideographic space',
  '\ufeff': 'zero-width no-break space',
  '-': 'hyphen-minus',
}

/** Shown blank or not at all; the settings draw a named box in their place. */
const INVISIBLE = /^[\s\u00ad\u034f\u200b-\u200f\u2060\ufeff]+$/u

export interface CharacterPart {
  text: string
  /** A name, for a character that's invisible or easily mistaken. */
  name: string | null
  invisible: boolean
}

/** A text character by character, naming the ones that need it. */
export function describeCharacters(text: string): CharacterPart[] {
  return graphemes(text).map((part) => ({
    text: part,
    name: NAMES[part] ?? null,
    invisible: INVISIBLE.test(part),
  }))
}

/** A text as a label reads it: “✓”, or its name when it can't be seen ("no-break space"). */
function characterTextLabel(text: string): string {
  if (!text) return 'nothing yet'
  const parts = describeCharacters(text)
  if (parts.every((part) => part.invisible)) return parts.map((part) => part.name ?? 'invisible character').join(' + ')
  return `“${text}”`
}

/** The action's name in the shortcut lists: Type “✓”, or Type “—”, then “–”. */
export function characterKeyLabel(slot: CharacterKey): string {
  const texts = characterTexts(slot)
  if (!texts.length) return 'Type (no characters yet)'
  return `Type ${texts.map(characterTextLabel).join(', then ')}`
}

// ── Whether a character shows on every device ───────────────────────────────

const RANGES = coverageRanges as [number, number][]

/** Ink Symbols, or ASCII, which every font has, draws this code point. */
function drawnEverywhere(cp: number): boolean {
  if (cp >= 0x20 && cp < 0x7f) return true
  let lo = 0
  let hi = RANGES.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (cp < RANGES[mid][0]) hi = mid - 1
    else if (cp > RANGES[mid][1]) lo = mid + 1
    else return true
  }
  return false
}

// Joiners and variation selectors take their look from the character before them.
const MODIFIER = /^[\u200b-\u200f\u2060\ufe00-\ufe0f\ufeff\u{e0020}-\u{e007f}\u{1f3fb}-\u{1f3ff}]$/u
const EMOJI = /\p{Emoji_Presentation}|\p{Extended_Pictographic}\ufe0f|\p{Regional_Indicator}/u

export interface CharacterCoverage {
  /** Some character is drawn by the system's emoji font, which differs between systems. */
  emoji: boolean
  /** Some character comes from whatever fonts the device has, and may be missing on another. */
  deviceFonts: boolean
}

export function characterCoverage(text: string): CharacterCoverage {
  let emoji = false
  let deviceFonts = false
  for (const part of graphemes(text)) {
    if (EMOJI.test(part)) {
      emoji = true
      continue
    }
    for (const char of part) {
      if (MODIFIER.test(char)) continue
      if (!drawnEverywhere(char.codePointAt(0)!)) deviceFonts = true
    }
  }
  return { emoji, deviceFonts }
}
